import { MESSAGE_VUNGLE_CLICKTHROUGH } from "./messages";
import Logger from "../logger";

// Set this to `true` in development to log all messages passing through
// Messenger.
const ENABLE_DEV_LOGGING = false;

const makeLiftoffMessage = (tag, data = []) => {
  return {
    isLiftoffMessage: true,
    tag,
    data,
  };
};

export const postLiftoffMessage = (targetWindow, tag, data = []) => {
  const message = makeLiftoffMessage(tag, data);
  targetWindow.postMessage(message, "*");
};

const getMessageTag = (message) => {
  return message.tag;
};

const getMessageData = (message) => {
  return message.data;
};

const isLiftoffMessage = (message) => {
  return typeof message === "object" && message.isLiftoffMessage === true;
};

const vungleMessages = new Set([MESSAGE_VUNGLE_CLICKTHROUGH]);
const isVungleMessage = (message) => {
  return typeof message === "string" && vungleMessages.has(message);
};

const isMessageSafe = (message, targetWindow) => {
  // NOTE(Austin): We don't check the origin because our messages
  // can potentially be cross-origin.
  return (
    message.source === targetWindow ||
    (message.data &&
      typeof message.data === "object" &&
      message.data.type === "logCustomLiftoffAnalytic")
  );
};

const warn = (name, message) => {
  console.warn(`[${name}] ${message}`);
};

const log = (name, message) => {
  if (!ENABLE_DEV_LOGGING) return;
  console.log(`[${name}] ${message}`);
};

export class Messenger {
  // #if process.env.NODE_ENV === "test"
//   /**
//    * Removes all mutations made by the messenger. Used for testing purposes.
//    */
//   reset() {
//     window.removeEventListener("message", this.handleMessage);
//   }
  // #endif

  /**
   * Constructs a messenger for listening and sending messages across different
   * windows (cross-origin). Arguments:
   * - targetWindow: The window you want to send to and receive from.
   * - name (optional): A name for this messenger for logging/debugging purposes.
   */
  constructor(targetWindow, name = "default") {
    this.name = name;
    this._callbacksByTag = {};
    this._targetWindow = targetWindow;

    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener("message", this.handleMessage);
  }

  handleMessage(rawMessage) {
    const { data: messageData } = rawMessage;
    if (!isMessageSafe(rawMessage, this._targetWindow)) {
      warn(
        this.name,
        `Received untrusted message: ${JSON.stringify(messageData)}`
      );
      return;
    }

    if (isLiftoffMessage(messageData)) {
      this.handleLiftoffMessage(messageData);
      return;
    }

    if (isVungleMessage(messageData)) {
      this.handleVungleMessage(messageData);
      return;
    }

    // Log custom event from a C.Studio custom playable.
    if (messageData.type === "logCustomLiftoffAnalytic") {
      Logger.recordVXOnlyAdexpMetric(messageData.analyticsType, 1);
      return;
    }

    warn(
      this.name,
      `Received unrecognized message: ${JSON.stringify(messageData)}`
    );
  }

  handleVungleMessage(message) {
    const messageTag = message;
    log(this.name, `Received Vungle message ${messageTag}`);
    const callbacks = this._callbacksByTag[messageTag] || [];
    callbacks.forEach((cb) => {
      cb();
    });
  }

  handleLiftoffMessage(message) {
    const messageTag = getMessageTag(message);
    const callbacks = this._callbacksByTag[messageTag] || [];
    const messageData = getMessageData(message);
    log(this.name, `Received ${messageTag}: ${JSON.stringify(messageData)}`);
    if (!Array.isArray(messageData)) {
      warn(this.name, `Received liftoff message data that is not in an array.`);
    }
    callbacks.forEach((cb) => {
      cb(...messageData);
    });
  }

  post(tag, ...data) {
    log(this.name, `Posting ${tag}: ${JSON.stringify(data)}`);
    postLiftoffMessage(this._targetWindow, tag, data);
  }

  on(tag, callback) {
    if (this._callbacksByTag[tag] == null) {
      this._callbacksByTag[tag] = [];
    }

    this._callbacksByTag[tag].push(callback);
  }

  off(tag, callback) {
    if (this._callbacksByTag[tag] == null) {
      return;
    }
    this._callbacksByTag[tag] = this._callbacksByTag[tag].filter((cb) => {
      return cb !== callback;
    });
  }

  once(tag, callback) {
    const singleCallback = (...args) => {
      // Important to pass the self-reference here and not `callback`.
      this.off(tag, singleCallback);
      callback(...args);
    };
    this.on(tag, singleCallback);
  }
}

export const makeIframeMessenger = (iframeEl, name = "default") => {
  const targetWindow = iframeEl.contentWindow;
  if (targetWindow == null) {
    throw new Error(
      [
        "Cannot construct iframe messenger:",
        "the iframe's content window is not initialized",
      ].join(" ")
    );
  }

  return new Messenger(targetWindow, name);
};

export const makeParentMessenger = (name = "default") => {
  const parentWindow = window.parent;
  if (parentWindow == null) {
    throw new Error(
      "Cannot initialize a parent messenger in a context without a parent"
    );
  }

  return new Messenger(parentWindow, name);
};



// WEBPACK FOOTER //
// ./src/js/madlib/csp-env/messenger.js